import {
	Button,
	Flex,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Stack,
	Text,
	useToast
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { Input } from "../Inputs/Input";
import { Select } from "../Inputs/InputSelect";
import { useCart } from "../../hooks/cart";
import { formatPrice } from "../../utils/format";

type PayFormData = {
	tipo: string;
	parcelas: string;
	valor: string;
}
type PayData = {
	id: string;
	band_pay: string;
	chave_pay: string;
	currency_pay: string;
	id_pay: number;
	max_parcel: number;
	method_pay: string;
	name_pay: string;
	qtd_parcel: number;
	tipchave_pay: string;
	trocoFormatted: string;
	troco_para: number;
	type_pay: string;
	conf: string;
	url_pay: string;
	vlr_pay: number;
}
interface PayModalParams {
	isOpen: boolean;
	onRequestClose: (close: boolean) => void;
	pays: PayData[];
}

const payFormSchema: yup.SchemaOf<PayFormData> = yup.object().shape({
	tipo: yup.string().required('Forma de pagamento obrigatório'),
	parcelas: yup.string().required('Parcelas obrigatória'),
	valor: yup.string().required('Valor obrigatória'),
});

const PayModal: React.FC<PayModalParams> = ({
	isOpen,
	onRequestClose,
	pays,
}) => {
	const { cart, addCartPagamento } = useCart();
	const toast = useToast();

	const [auxPays, setAuxPays] = useState<{ value: number, label: string }[]>([]);
	const [selectPay, setSelectPay] = useState<PayData>({} as PayData);

	const { register, handleSubmit, formState, setValue } = useForm<PayFormData>({
		resolver: yupResolver(payFormSchema)
	});

	useEffect(() => {
		const aux = pays.map(pay => {
			return {
				value: pay.id_pay,
				label: pay.name_pay
			}
		});

		setAuxPays(aux);
		setValue('parcelas', '1');
		setValue('valor', String(cart.falta).replace('.', ','));
		// handleFormatMoeda(String(cart.falta).replace('.', ','));
	}, []);

	function handleFormatMoeda(event: string) {
		let moedastr = event;
		moedastr = moedastr.replace(/\D/g, '');

		if (moedastr) {
			const moeda = parseInt(moedastr.replace(/[\D]+/g, ''));

			let tmp = `${moeda}`;
			tmp = tmp.replace(/([0-9]{2})$/g, ',$1');
			if (tmp.length > 6)
				tmp = tmp.replace(/([0-9]{3}),([0-9]{2}$)/g, '.$1,$2');

			setValue('valor', `${tmp}`);
		} /* else {
			moedastr = moedastr.replace(/\D/g, '');
			setValue('valor', `R$ ${moedastr}`);
		} */
	};
	function handleFormatParcelas(event: string) {
		const ret = event.replace(/\D/g, '');
		setValue('parcelas', ret);

		if (selectPay && Number(ret) > selectPay.max_parcel) {
			setValue('parcelas', '');

			toast({
				title: 'Permissão.',
				description: `Maximo de Parcelas permitido para esse pagamento é ${selectPay.max_parcel}.`,
				status: 'error',
				duration: 5000,
				position: 'top-right',
				isClosable: true,
			});
		}
	}

	function handleSelectPay(event: string) {
		const indexPay = pays.findIndex(pay => pay.id_pay === Number(event));
		setSelectPay(pays[indexPay]);
	}

	const handlePagamento: SubmitHandler<PayFormData> = async (data) => {
		// const indexPay = pays.findIndex(pay => pay.id_pay === Number(data.tipo));
		const auxPay = selectPay;

		auxPay.qtd_parcel = Number(data.parcelas);
		const valor = data.valor.replace(',', '.');
		auxPay.vlr_pay = Number(valor);

		if (auxPay.vlr_pay > cart.falta) {
			if (auxPay.method_pay !== 'CASH') {
				toast({
					title: 'Permissão.',
					description: `Valor do pagamento maior do que falta.`,
					status: 'error',
					duration: 5000,
					position: 'top-right',
					isClosable: true,
				});

				return;
			}
		}

		addCartPagamento(auxPay);
		onRequestClose(false);
	};

	return (
		<Modal
			isCentered={true}
			size="xl"
			isOpen={isOpen}
			onClose={() => onRequestClose(false)}
		>
			<ModalOverlay />

			<ModalContent
				as="form"
				mt={["auto", "22"]}
				mx="4"
				onSubmit={handleSubmit(handlePagamento)}
			>
				<ModalCloseButton />

				<ModalHeader mb="0" pb="0">Adicionar Pagamento</ModalHeader>

				<ModalBody>
					<Flex
						flexDir="column"
						p="2"
						mb="2"
						borderWidth={1}
						borderColor="gray.100"
						borderRadius={8}
					>
						<Flex justify="space-between">
							<Text fontWeight="500" color="gray.400">SubTotal</Text>
							<Text fontWeight="500" color="gray.400">{formatPrice(cart.subTotal)}</Text>
						</Flex>

						<Flex justify="space-between">
							<Text fontWeight="500" color="gray.400">Troco</Text>
							<Text fontWeight="500" color="gray.400">{formatPrice(cart.troco)}</Text>
						</Flex>

						<Flex justify="space-between">
							<Text fontWeight="500">Total</Text>
							<Text fontWeight="500">{formatPrice(cart.total)}</Text>
						</Flex>

						<Flex justify="space-between">
							<Text fontWeight="500" color="gray.500">Falta</Text>
							<Text fontWeight="500" color="gray.500">{formatPrice(cart.falta)}</Text>
						</Flex>
					</Flex>

					<Stack spacing="4">
						<Select
							label="Forma Pagamento"
							options={auxPays}
							{...register("tipo")}
							error={formState.errors.tipo}
							onChange={e => handleSelectPay(e.target.value)}
							defaultValue=""
						/>

						<Input
							label={`Qtd Parcelas ${selectPay.id_pay ? '(maximo: ' + selectPay.max_parcel +')' : ''}`}
							maxLength={2}
							{...register("parcelas")}
							error={formState.errors.parcelas}
							onChange={e => handleFormatParcelas(e.target.value)}
							isDisabled={!selectPay.id_pay}
						/>

						<Input
							label="Valor"
							{...register("valor")}
							error={formState.errors.valor}
							onChange={(e: any) => handleFormatMoeda(e.target.value)}
							isDisabled={!selectPay.id_pay}
						/>
					</Stack>
				</ModalBody>

				<ModalFooter pt="2">
					<Button colorScheme='blue' ml="4" type="submit">
						Adicionar
					</Button>

					<Button
						colorScheme="blackAlpha"
						ml="4"
						onClick={() => onRequestClose(false)}
					>
						Cancelar
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	)
}

export default PayModal;
